import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { auth } from '@/core/auth/authService';
import { loginErrorMessage, NO_TENANT_MESSAGE, safeReturnUrl } from './loginMessages';
import './login.scss';

type Step = 'credentials' | 'new-password';

/** The app's own sign-in page: username and password, plus the set-a-password step of a first login. */
export function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [step, setStep] = useState<Step>('credentials');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const continueToApp = () => navigate(safeReturnUrl(params.get('returnUrl')));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (params.get('reason') === 'no-tenant') {
        setError(NO_TENANT_MESSAGE);
      } else if (await auth.isAuthenticated()) {
        if (!cancelled) navigate(safeReturnUrl(params.get('returnUrl')));
        return;
      }
      if (!cancelled) setCheckingSession(false);
    })();
    return () => {
      cancelled = true;
    };
    // Angular ran this once on init.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    try {
      await action();
    } catch (e) {
      setError(loginErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setError('');

    if (step === 'credentials') {
      if (!username.trim() || !password) {
        setError('Enter your username and password.');
        return;
      }
      await run(async () => {
        const result = await auth.signIn(username.trim(), password);
        if (result === 'new-password-required') {
          setStep('new-password');
          setNotice('Welcome. Choose a new password to finish setting up your account.');
        } else {
          continueToApp();
        }
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('The two passwords do not match.');
      return;
    }
    await run(async () => {
      await auth.completeNewPassword(newPassword);
      continueToApp();
    });
  };

  const backToSignIn = () => {
    setStep('credentials');
    setNotice('');
    setError('');
    setPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const toggleShow = () => setShowPassword((shown) => !shown);

  return (
    <div className="login-host">
      <div className="login-page">
        <aside className="brand-panel" aria-hidden="true">
          <span className="orb orb-1"></span>
          <span className="orb orb-2"></span>
          <span className="orb orb-3"></span>
          <div className="brand-content">
            <img className="brand-logo" src="assets/images/logo.png" alt="" />
            <p className="brand-product">Accounts Production</p>
          </div>
        </aside>

        <main className="form-panel">
          {!checkingSession && (
            <section className="card">
              <img className="card-logo" src="assets/images/logo.png" alt="Capium" />

              <h1>{step === 'credentials' ? 'Sign in' : 'Set your password'}</h1>
              {step === 'credentials' && <p className="lead">Use your Capium username and password.</p>}
              {step === 'new-password' && notice && <p className="lead">{notice}</p>}

              {error && (
                <div className="alert" role="alert">
                  {error}
                </div>
              )}

              <form onSubmit={submit} noValidate>
                {step === 'credentials' && (
                  <>
                    <div className="field">
                      <label htmlFor="username">Username</label>
                      <input
                        id="username"
                        name="username"
                        type="text"
                        autoComplete="username"
                        autoCapitalize="none"
                        spellCheck={false}
                        maxLength={320}
                        autoFocus
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        disabled={busy}
                        placeholder="Username or email"
                      />
                    </div>

                    <div className="field">
                      <label htmlFor="password">Password</label>
                      <div className="password-wrap">
                        <input
                          id="password"
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="current-password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          disabled={busy}
                          placeholder="Password"
                        />
                        <button
                          type="button"
                          className="toggle"
                          onClick={toggleShow}
                          aria-pressed={showPassword}
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? 'Hide' : 'Show'}
                        </button>
                      </div>
                    </div>
                  </>
                )}

                {step === 'new-password' && (
                  <>
                    <div className="field">
                      <label htmlFor="new-password">New password</label>
                      <input
                        id="new-password"
                        name="newPassword"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        disabled={busy}
                        placeholder="New password"
                      />
                    </div>
                    <div className="field">
                      <label htmlFor="confirm-password">Confirm new password</label>
                      <div className="password-wrap">
                        <input
                          id="confirm-password"
                          name="confirmPassword"
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          disabled={busy}
                          placeholder="Confirm new password"
                        />
                        <button type="button" className="toggle" onClick={toggleShow} aria-pressed={showPassword}>
                          {showPassword ? 'Hide' : 'Show'}
                        </button>
                      </div>
                    </div>
                  </>
                )}

                <button className="submit" type="submit" disabled={busy}>
                  {busy && <span className="spinner"></span>}
                  <span>{busy ? 'Please wait' : step === 'credentials' ? 'Sign in' : 'Save password and sign in'}</span>
                </button>

                {step === 'new-password' && (
                  <button className="link" type="button" onClick={backToSignIn} disabled={busy}>
                    Back to sign in
                  </button>
                )}
              </form>
            </section>
          )}
        </main>
      </div>
    </div>
  );
}
